import {MEDIUM_BREAKPOINT} from '@wandb/weave/common/css/breakpoints.styles';
import {Button} from '@wandb/weave/components/Button';
import classNames from 'classnames';
import * as _ from 'lodash';
import memoize from 'memoize-one';
import * as React from 'react';
import {useInView} from 'react-intersection-observer';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import {Analytics} from '../../../services/analytics';
import {RunWithRunsetInfo} from '../../../state/runs/types';
import {useViewer} from '../../../state/viewer/hooks';
import * as Filter from '../../../util/filters';
import * as FilterTypes from '../../../util/filterTypes';
import {hotkeyPressed, hotkeyString} from '../../../util/hotkeys';
import {Sort as QuerySort} from '../../../util/queryTypes';
import {
  Config as TableSettings,
  EMPTY_CONFIG as EMPTY_TABLE_SETTINGS,
} from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import * as RunTypes from '../../../util/runTypes';
import * as TableCols from '../../../util/tablecols';
import {getColumnWidth} from '../../../util/wbtree';
import {ModalTriggerButton} from '../../elements/ModalTriggerButton';
import {
  NAV_AND_VIEW_BAR_OFFSET,
  PROJECT_BAR_OFFSET,
} from '../../MultiRunWorkspace/constants';
import {useRunSelectorResizingContext} from '../../MultiRunWorkspace/RunSelectorResizingContext';
import {useWorkspaceDrawerContextMaybe} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {
  WBTableColumn,
  WBTableGridContainerProps,
  WBTableProps,
  WBTableRowFields,
} from './types';
import WBTableColumnEditor from './WBTableColumnEditor';
import {WBTableGridContainer} from './WBTableGridContainer';
import {WBTablePagination} from './WBTablePagination';

const RUNS_SELECTOR_BORDER_WIDTH = 1;

interface WBTableState {
  columnDragAccessor?: string;
  columnDropAccessor?: string;
  currentPage: number;
  scrolledHorizontally: boolean;
  scrolledVertically: boolean;
  scrollXBounds: [number, number];
  childrenPaginationMap: {[key: string]: number | undefined}; // maps row.__address__ to the active page for each paginated group, like {groupId2 => 2, groupId2 => 5}
  expandedRowAddresses: string[];

  // expandability
  currentClientX: number;
  dragInitialValues: {
    clientX: number;
    width: number;
    runNameColumnWidth: number;
    maxPinnedColumnsWidth: number;
  };
  dragCurrentValues: {
    runNameColumnWidth: number;
    clientX: number;
  } | null;
  scrollY: number;
  columnResizeOffset: number;
  viewportWidth: number;
  viewportHeight: number;
}

type AllWBTableProps = WBTableProps & ReturnType<typeof useWBTableProps>;

export class WBTableComp extends React.Component<
  AllWBTableProps,
  WBTableState
> {
  static getDerivedStateFromProps(
    props: AllWBTableProps,
    state: WBTableState
  ): null | Partial<WBTableState> {
    if (props.loading && _.isEmpty(props.rows)) {
      return null;
    }

    const expandedRowAddresses =
      props.expandedRowAddresses || state.expandedRowAddresses || [];
    return {
      expandedRowAddresses,
    };
  }

  scrollRef = React.createRef<HTMLDivElement>();
  scrollInterval?: any;

  treeRef = React.createRef<HTMLDivElement>();
  pinnedTreeRef = React.createRef<HTMLDivElement>();
  unpinnedTreeRef = React.createRef<HTMLDivElement>();

  containerRef = React.createRef<HTMLDivElement>();
  wrapperRef: React.MutableRefObject<HTMLDivElement | null> = React.createRef();

  currentWidth = 0;
  snapToWidths: number[] = [];

  visibleColumnsInternal = memoize(
    (tableSettings: TableSettings, columns: WBTableColumn[]) => {
      return TableCols.getOrderedColumns(tableSettings, columns);
    }
  );

  tableSettingsInternal = memoize(
    (tableSettings: TableSettings, columns: WBTableColumn[]) => {
      return TableCols.initializeTableSettings(tableSettings, columns);
    }
  );

  sortableColumnsInternal = memoize((columns: WBTableColumn[]) => {
    return this.props.isColumnSortable
      ? columns.filter(this.props.isColumnSortable)
      : columns;
  });

  rowsInternal = memoize(
    (
      pageSize: number,
      currentPage: number,
      rows: Array<WBTableRowFields<RunWithRunsetInfo>>
    ) => {
      const pageStart = (currentPage - 1) * pageSize;
      const pageEnd = currentPage * pageSize;
      const allRows = rows || [];
      // if rows don't have unique ids, add them
      if (allRows[0] && _.isUndefined(allRows[0].__address__)) {
        allRows.forEach((r, i) => (r.__address__ = i + ''));
      }
      return allRows.slice(pageStart, pageEnd);
    }
  );

  // duplicated from WBTableGridContainer; consider de-duping
  pinnedColumns = memoize(
    (tableSettings: TableSettings, columns: WBTableColumn[]) => {
      return columns.filter(c => TableCols.isPinned(tableSettings, c.accessor));
    }
  );

  updateScrollXBounds = _.throttle(
    () => {
      const node = this.scrollRef.current;
      if (node == null) {
        return;
      }

      const scrollXBounds: [number, number] = [
        node.scrollLeft,
        node.scrollLeft + node.offsetWidth,
      ];
      if (!_.isEqual(scrollXBounds, this.state.scrollXBounds)) {
        this.setState({scrollXBounds});
      }
    },
    300,
    {leading: false}
  );

  handleResize = _.throttle(() => {
    this.setState({
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
  }, 200);

  constructor(props: AllWBTableProps) {
    super(props);
    this.state = {
      columnDragAccessor: undefined,
      columnDropAccessor: undefined,
      currentPage: 1,
      scrolledHorizontally: false,
      scrolledVertically: false,
      scrollXBounds: [0, 4000],
      childrenPaginationMap: {},
      expandedRowAddresses: [],

      // expandability
      currentClientX: 0,
      dragInitialValues: {
        clientX: 0,
        width: 0,
        runNameColumnWidth: 0,
        maxPinnedColumnsWidth: 0,
      },
      dragCurrentValues: null,
      scrollY: 0,
      columnResizeOffset: 0,
      viewportWidth: 0,
      viewportHeight: 0,
    };
  }

  maxPage = () => {
    return Math.ceil(
      (this.props.totalRowCount || this.props.rows.length) / this.pageSize()
    );
  };

  pageSize = () => {
    return this.tableSettings().pageSize || EMPTY_TABLE_SETTINGS.pageSize;
  };

  tableSettings = () => {
    const settings = this.tableSettingsInternal(
      this.props.tableSettings || EMPTY_TABLE_SETTINGS,
      this.props.columns
    );
    settings.columnWidths = {
      ...settings.columnWidths,
      // See handleMouseMove for why we do things this way. This is purely a
      // performance workaround for large workspaces.
      'run:displayName': this.props.isResizingFullHeight
        ? this.state.dragCurrentValues!.runNameColumnWidth
        : settings.columnWidths['run:displayName'],
    };
    return settings;
  };

  visibleColumns = () => {
    return this.visibleColumnsInternal(
      this.tableSettings(),
      this.props.columns
    );
  };

  columnKeySupportsAggregates = (key: RunTypes.Key) => {
    if (Run.isAggregationColumn(key.section)) {
      return true;
    }
    // if the column is summary:acc we may have the aggregation if the value is a number
    // so we check to see if aggregation_min:acc or aggregation_max:acc exists
    for (const column of this.props.columns) {
      if (
        column.key.name === key.name &&
        Run.isAggregationColumn(column.key.section)
      ) {
        return true;
      }
    }
    return false;
  };

  sortableColumns = () => {
    return this.sortableColumnsInternal(this.props.columns);
  };

  grouping = () => {
    return this.props.grouping || [];
  };

  rows = () => {
    return this.rowsInternal(
      this.pageSize(),
      this.state.currentPage,
      this.props.rows
    );
  };

  resetPagination = () => {
    if (this.props.clearTableMessage) {
      this.props.clearTableMessage();
    }
    this.setCurrentPage(1);
  };

  setCurrentPage = (currentPage: number) => {
    this.setState({currentPage});
    this.props.onSetCurrentPage?.(currentPage);
  };

  // sets the pagination (active page) for the given rowAddress
  setChildrenPagination = (rowAddress: string, activePage?: number) => {
    return this.setState({
      childrenPaginationMap: {
        ...this.state.childrenPaginationMap,
        [rowAddress]: activePage,
      },
    });
  };

  // open or close the row at the given address (rowAddress looks like '1-6-5-1-5')
  toggleExpandedRow = (rowAddress: string) => {
    if (this.props.toggleExpandedRow) {
      this.props.toggleExpandedRow(rowAddress);
    } else {
      const {expandedRowAddresses} = this.state;
      this.setState({
        expandedRowAddresses: _.includes(expandedRowAddresses, rowAddress)
          ? // remove rowAddress from expandedRowAddresses
            expandedRowAddresses.filter(a => a !== rowAddress)
          : // add rowAddress to expandedRowAddresses
            expandedRowAddresses.concat([rowAddress]),
      });
    }
  };

  setTableState = (
    stateUpdate: Pick<
      WBTableState,
      'columnDragAccessor' | 'columnDropAccessor' | 'columnResizeOffset'
    >
  ) => {
    this.setState(stateUpdate);
  };

  handleMouseMove = (e: MouseEvent) => {
    if (this.props.isResizingFullHeight) {
      const initialValues = this.state.dragInitialValues;
      const draggedWidth = this.getDraggedWidth();
      const addedWidth = draggedWidth - initialValues.maxPinnedColumnsWidth;

      const runNameColumnWidth =
        draggedWidth < initialValues.runNameColumnWidth
          ? draggedWidth
          : initialValues.runNameColumnWidth + addedWidth;

      this.setState({
        currentClientX: e.clientX,
        // When dragging, we move some column width state into the component to
        // improve render performance in large workspaces. Instead of rendering
        // the runs column from redux state, we get the width from local
        // component state, while the user is resizing that column. Once the
        // user releases the mouse button, the column width is propagated up to
        // redux.
        dragCurrentValues: {
          runNameColumnWidth,
          clientX: e.clientX,
        },
      });
    }
  };

  getDraggedWidth = () =>
    this.state.dragInitialValues.width +
    this.state.currentClientX -
    this.state.dragInitialValues.clientX;

  setColumnWidth = (accessor: string, value: number) => {
    const tableSettings = this.tableSettings();
    this.setTableSettings({
      ...tableSettings,
      columnWidths: {
        ...tableSettings.columnWidths,
        [accessor]: value,
      },
    });
  };

  computeDestinedIndex = () => {
    const draggedWidth = this.getDraggedWidth();
    let closestIndex = 0;
    this.snapToWidths.forEach((width, index) => {
      if (
        Math.abs(draggedWidth - width) <
        Math.abs(draggedWidth - this.snapToWidths[closestIndex])
      ) {
        closestIndex = index;
      }
    });
    return closestIndex;
  };

  handleMouseUp = () => {
    if (this.props.isResizingFullHeight) {
      this.setTableSettings({
        ...this.tableSettings(),
        pinnedColumnsShown: this.computeDestinedIndex() + 1,
      });

      const initialWidth = this.state.dragInitialValues.width;
      if (this.currentWidth !== initialWidth) {
        // NOTE: This *will not* detect changes from resizing pinned column headers.
        // It only tracks if the resizer for the runs selector as a whole was used.
        Analytics.track('Runs selector resized', {
          initialWidth,
          updatedWidth: this.currentWidth,
        });
      }

      // User has stopped dragging, so propagate the runs list column width to
      // the redux state via the `setColumnWidth` method. Then reset the drag
      // current values. See handleMouseMove for why we do things this way.
      const runNameColumnWidth =
        this.state.dragCurrentValues!.runNameColumnWidth;
      this.setColumnWidth('run:displayName', runNameColumnWidth);
      this.props.setIsResizingFullHeight(false);
      this.setState({
        dragCurrentValues: null,
      });

      // trick panels into refreshing layout
      window.dispatchEvent(new Event('resize'));
    }
  };

  handleScroll = () => {
    this.setState({scrollY: window.scrollY});
  };

  columnEditor = () => {
    const makeColumnEditor =
      this.props.columnEditor ||
      ((tableSettings, setTableSettings) => (
        <WBTableColumnEditor
          allColumns={this.props.columns.map(column => column.accessor)}
          fixedColumns={this.props.fixedColumns}
          config={tableSettings}
          update={setTableSettings}
        />
      ));
    return makeColumnEditor(this.tableSettings(), this.props.setTableSettings);
  };

  componentDidMount() {
    const scrollRef = this.scrollRef.current;
    if (scrollRef) {
      scrollRef.addEventListener('mousewheel', this.preventBackScroll);
    }

    // expandability
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('keydown', this.handleGlobalKeyDown);
    window.addEventListener('resize', this.handleResize);

    this.props.onSetCurrentPage?.(this.state.currentPage);
  }

  componentWillUnmount() {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = undefined;
    }

    // expandability
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('keydown', this.handleGlobalKeyDown);

    if (this.scrollRef.current) {
      this.scrollRef.current.removeEventListener(
        'mousewheel',
        this.preventBackScroll
      );
    }
  }

  handleGlobalKeyDown = (e: KeyboardEvent) => {
    // alt+space shortcut to expand table
    if (hotkeyPressed(e, 'toggleRunsSidebar')) {
      this.toggleExpanded();
    }
  };

  // This guys prevents the browser backwards behavior some OS Browser combos have for the table.
  preventBackScroll = (e: any) => {
    if (!this.scrollRef.current) {
      return;
    }

    const node = this.scrollRef.current;
    const maxX = node.scrollWidth - node.offsetWidth;

    // If node event looks like it will scroll beyond the bounds of the element, prevent it and set the scroll to the boundary manually
    if (
      // Must be scrollable
      node.offsetWidth !== node.scrollWidth &&
      // More horizontal movement than vertical; this is a hack that sort of works
      Math.abs(e.deltaX) >= Math.abs(e.deltaY) &&
      // Maxed out left side
      ((node.scrollLeft <= 0 && e.deltaX < 0) ||
        // Maxed out right side
        node.scrollLeft + node.offsetWidth >= node.scrollWidth)
    ) {
      e.preventDefault();
      // Manually set the scroll to the boundary
      node.scrollLeft = Math.max(0, Math.min(maxX, node.scrollLeft + e.deltaX));
      node.scrollTop = node.scrollTop + e.deltaY;
    }
  };

  setTableSettings = (newSettings: TableSettings) => {
    this.props.setTableSettings(newSettings);
  };

  moveColumn = () => {
    const fromAccessor = this.state.columnDragAccessor;
    const toAccessor = this.state.columnDropAccessor;
    if (fromAccessor && toAccessor && fromAccessor !== toAccessor) {
      this.setTableSettings(
        TableCols.moveBefore(this.tableSettings(), fromAccessor, toAccessor)
      );
      this.setState({
        columnDragAccessor: undefined,
        columnDropAccessor: undefined,
      });
    }
  };

  togglePinnedColumn = (columnAccessor: string): void => {
    this.setTableSettings(
      TableCols.togglePinned(this.tableSettings(), columnAccessor)
    );
  };

  hideColumn = (columnAccessor: string): void => {
    this.setTableSettings(
      TableCols.hideColumns(this.tableSettings(), [columnAccessor])
    );
  };

  replaceColumnAggregation = (
    oldColumnAccessor: string,
    newColumnAccessor: string
  ): void => {
    this.setTableSettings(
      TableCols.replaceColumnAggregation(
        this.tableSettings(),
        oldColumnAccessor,
        newColumnAccessor
      )
    );
  };

  addFilter = (
    key: RunTypes.Key,
    op: FilterTypes.ValueOp,
    value: RunTypes.Value
  ) => {
    const filters = this.props.filters || Filter.EMPTY_FILTERS;
    const {setFilters} = this.props;
    if (setFilters == null) {
      return;
    }

    // Check if "createdAt <=" filter already exists.
    // If so, modify that filter instead of adding a new one.
    if (Filter.isFreezeFilter({key, op, value})) {
      const freezeFilterIndex = Filter.findFreezeFilterIndex(filters);
      if (freezeFilterIndex > -1) {
        const freezeFilter = filters.filters[0].filters[freezeFilterIndex];
        setFilters(
          Filter.Update.setFilter(filters, [0, freezeFilterIndex], {
            ...freezeFilter,
            value: value as string,
            disabled: false,
          } as FilterTypes.IndividualFilter<RunTypes.Key>)
        );
        return;
      }
    }

    // Add the new filter
    setFilters(
      Filter.Update.groupPush(filters, [0], {
        key,
        op,
        value,
      })
    );
  };

  updateSort = (updateFn: (sort: QuerySort) => void) => {
    if (this.props.updateSort) {
      this.props.updateSort(updateFn);
    } else {
      // TODO: actually implement local sorting
      // this.setState(updateFn);
    }
    this.resetPagination();
  };

  setGrouping = (newGrouping: RunTypes.Key[]) => {
    this.props.setGrouping(newGrouping);
    this.resetPagination();
  };

  addGroup = (newGroup: RunTypes.Key) => {
    this.setGrouping([...this.grouping(), newGroup]);
  };

  toggleExpanded = () => {
    const expanded = this.props.expanded;
    if (this.props.expandable) {
      if (this.props.onSetExpanded) {
        this.props.onSetExpanded(!expanded);
      }
    }
  };

  onSliderMouseDown = (e: any) => {
    e.preventDefault();
    const runNameColumnWidth = getColumnWidth(this.tableSettings(), {
      section: 'run',
      name: 'displayName',
    });

    this.props.setIsResizingFullHeight(true);
    this.setState({
      currentClientX: e.clientX,
      dragInitialValues: {
        width: this.currentWidth,
        clientX: e.clientX,
        runNameColumnWidth,
        maxPinnedColumnsWidth:
          this.snapToWidths[this.snapToWidths.length - 1] +
          RUNS_SELECTOR_BORDER_WIDTH,
      },
      dragCurrentValues: {
        runNameColumnWidth,
        clientX: e.clientX,
      },
    });
  };

  updateScroll = () => {
    if (this.scrollRef.current == null) {
      return;
    }
    const x = this.scrollRef.current.scrollLeft;
    const y = this.scrollRef.current.scrollTop;
    const newScrolled = {
      scrolledHorizontally: x > 0,
      scrolledVertically: y > 0,
    };
    if (
      newScrolled.scrolledHorizontally !== this.state.scrolledHorizontally ||
      newScrolled.scrolledVertically !== this.state.scrolledVertically
    ) {
      this.setState(newScrolled);
    }
    this.updateScrollXBounds();
  };

  dualRef = (element: HTMLDivElement | null) => {
    this.props.onScreenRef(element);
    this.wrapperRef.current = element;
  };

  render() {
    const {
      className,
      tableLoadMore,
      totalRowCount,
      tableMessage,
      clearTableMessage,
      extraTableActionsLeft,
      extraTableActionsRight,
      fixedColumns,
      openSortPopup,
      onScreen,
      columnResizingAccessor,
    } = this.props;
    const loading = this.props.loading || false;
    const {
      scrolledHorizontally,
      scrolledVertically,
      scrollXBounds,
      currentPage,
      childrenPaginationMap,
      expandedRowAddresses,
      columnDragAccessor,
      columnDropAccessor,
    } = this.state;

    // /* SORTING */
    const visibleColumns = this.visibleColumns();
    const tableSettings = this.tableSettings();

    const pinnedColumns = this.pinnedColumns(tableSettings, visibleColumns);

    // fixed columns should always be pinned. if unpinned, it's because we
    // had a bug that allowed users to unpin fixed cols. automatically pin
    // those columns back & users won't be able to unpin anymore.
    fixedColumns?.forEach(fixedColumn => {
      const isPinned = this.tableSettings().columnPinned[fixedColumn];
      if (!isPinned) {
        this.togglePinnedColumn(fixedColumn);
      }
    });

    this.snapToWidths = [];
    let currentSnapToWidth = 0;
    pinnedColumns.forEach(col => {
      currentSnapToWidth += getColumnWidth(tableSettings, col.key);
      this.snapToWidths.push(currentSnapToWidth);
    });
    const pinnedColumnsShown = tableSettings.pinnedColumnsShown
      ? Math.min(tableSettings.pinnedColumnsShown, pinnedColumns.length)
      : 1;
    this.currentWidth =
      this.snapToWidths[pinnedColumnsShown - 1] +
      this.state.columnResizeOffset +
      RUNS_SELECTOR_BORDER_WIDTH;

    const displayedWidth = this.props.isResizingFullHeight
      ? Math.max(
          this.state.dragInitialValues.width +
            // if resizing, dragCurrentValues.clientX will be defined
            this.state.dragCurrentValues!.clientX -
            this.state.dragInitialValues.clientX,
          this.snapToWidths[0]
        )
      : this.currentWidth;

    const destinedIndex = this.computeDestinedIndex();
    const destinedWidth = this.snapToWidths[destinedIndex];

    const maxSidebarWidth =
      this.snapToWidths[this.snapToWidths.length - 1] +
      RUNS_SELECTOR_BORDER_WIDTH +
      this.state.columnResizeOffset;

    // The wrapper width determines how squashed everything to the right of the sidebar is
    const wrapperWidth = Math.min(displayedWidth, maxSidebarWidth);

    // The container width is the width of what you see as the sidebar
    // the closing ternary here is a hack fix for a regression caused by #10996
    // it restores dynamic resizing of the sidebar on mobile
    const containerWidth = this.props.expanded
      ? 'calc(100vw - 52px)'
      : this.props.isResizingFullHeight && destinedWidth > displayedWidth
      ? destinedWidth
      : window?.innerWidth && window?.innerWidth > 768 // HACK
      ? displayedWidth
      : '100%';

    const gridProps: WBTableGridContainerProps = {
      loading: loading || false,
      columns: this.visibleColumns(),
      fixedColumns: fixedColumns || [],
      rows: this.rows(),
      addFilter: this.addFilter,
      updateSort: this.updateSort,
      updateTruncationType: this.props.updateTruncationType,
      addGroup: this.addGroup,
      expandedRowAddresses,
      toggleExpandedRow: this.toggleExpandedRow,
      tableSettings: this.tableSettings(),
      scrollXBounds,
      childrenPaginationMap,
      pinnedTreeRef: this.pinnedTreeRef,
      readOnly: this.props.disabledFeatures?.['edit-cols'],
      unpinnedTreeRef: this.props.expandable
        ? this.unpinnedTreeRef
        : this.scrollRef,
      expandable: this.props.expandable,
      expanded: this.props.expanded,
      isSingleMode: this.props.isSingleMode,
      isInReport: this.props.isInReport,
      showArtifactCounts: this.props.showArtifactCounts,
      showLogCounts: this.props.showLogCounts,
      topLevelQueryVariables: this.props.topLevelQueryVariables,
      SortIndicatorComponent: this.props.SortIndicatorComponent,
      setChildrenPagination: this.setChildrenPagination,
      setTableState: this.setTableState,
      setTableSettings: (settings: Partial<TableSettings>) => {
        this.setTableSettings({
          ...this.tableSettings(),
          ...settings,
        });
      },
      togglePinnedColumn: this.togglePinnedColumn,
      hideColumn: this.hideColumn,
      replaceColumnAggregation: this.replaceColumnAggregation,
      columnKeySupportsAggregates: this.columnKeySupportsAggregates,
      moveColumn: this.moveColumn,
      columnDragAccessor,
      columnDropAccessor,
      openSortPopup,
      truncationType: this.props.truncationType,
      sort: this.props.sort,
    };

    if (!onScreen) {
      return (
        <div
          ref={this.dualRef}
          style={{height: gridProps.rows.length * 44 + 138}}
        />
      );
    }

    const isTopActionsEmpty =
      this.props.disabledFeatures?.search &&
      this.props.disabledFeatures?.filter &&
      this.props.disabledFeatures?.group &&
      this.props.disabledFeatures?.sort &&
      this.props.disabledFeatures?.tag &&
      this.props.disabledFeatures?.move &&
      this.props.disabledFeatures?.delete &&
      this.props.disabledFeatures?.['create-sweep'] &&
      this.props.disabledFeatures?.export &&
      this.props.disabledFeatures?.['auto-cols'] &&
      this.props.disabledFeatures?.['edit-cols'];

    return (
      <div
        ref={this.dualRef}
        className={classNames('wb-table-wrapper', className, {
          expanded: this.props.expanded,
          'scrolled-horizontally': scrolledHorizontally,
          'scrolled-vertically': scrolledVertically,
        })}
        style={
          this.props.expandable
            ? {
                width: wrapperWidth,
              }
            : {}
        }>
        <div
          className={'wb-table-container'}
          ref={this.containerRef}
          style={
            this.props.expandable
              ? {
                  transitionDuration:
                    maxSidebarWidth < displayedWidth ||
                    columnResizingAccessor != null
                      ? '0s'
                      : undefined,
                  minWidth: containerWidth,
                  width: containerWidth,
                }
              : {}
          }>
          {/* TABLE ACTIONS */}
          <div
            className="wb-table-content"
            style={
              this.props.expandable
                ? {
                    height: `calc(100vh - ${
                      this.props.expanded &&
                      window.innerWidth < MEDIUM_BREAKPOINT
                        ? NAV_AND_VIEW_BAR_OFFSET + PROJECT_BAR_OFFSET
                        : NAV_AND_VIEW_BAR_OFFSET -
                          Math.min(this.state.scrollY, NAV_AND_VIEW_BAR_OFFSET)
                    }px)`,
                    WebkitMaskImage: this.props.isResizingFullHeight
                      ? destinedWidth < displayedWidth
                        ? `linear-gradient(to left, rgba(0,0,0,.5), rgba(0,0,0,.5) ${
                            displayedWidth - destinedWidth
                          }px, rgba(0,0,0,1) ${
                            displayedWidth - destinedWidth
                          }px)`
                        : `linear-gradient(to left, rgba(0,0,0,.5), rgba(0,0,0,.5) ${
                            destinedWidth - displayedWidth
                          }px, rgba(0,0,0,1) ${
                            destinedWidth - displayedWidth
                          }px)`
                      : undefined,
                  }
                : {}
            }>
            {!isTopActionsEmpty && (
              <div
                className="wb-table-top"
                style={
                  this.props.isResizingFullHeight ? {width: destinedWidth} : {}
                }>
                {this.props.expandable && (
                  <div className="wb-table-title-wrapper">
                    <span className="wb-table-title">{this.props.title}</span>
                    <Popup
                      inverted
                      closeOnTriggerClick
                      position="right center"
                      size="small"
                      className="keyboard-shortcut-popup"
                      trigger={
                        <Button
                          data-test="expand-toggle"
                          aria-label={
                            this.props.expanded
                              ? 'Collpase runs table'
                              : 'Expand runs table'
                          }
                          variant="ghost"
                          icon={
                            this.props.expanded
                              ? 'contract-left'
                              : 'expand-right'
                          }
                          onClick={this.toggleExpanded}
                        />
                      }>
                      {`${
                        this.props.expanded ? 'Collapse' : 'Expand'
                      } (${hotkeyString('toggleRunsSidebar')})`}
                    </Popup>
                  </div>
                )}
                <div className="wb-table-actions">
                  <span className="wb-table-actions-left">
                    {/* Filter, Group, Sort, Move, Delete, Tag, etc */}
                    {extraTableActionsLeft}
                  </span>
                  {(!this.props.expandable || this.props.expanded) && (
                    <span
                      className={classNames('wb-table-actions-right', {
                        // expandable RS wraps action btns under search bar; align to flex-end to ensure btns line up
                        'items-end': this.props.expandable,
                      })}>
                      {/* Auto Cols, etc */}
                      {extraTableActionsRight}
                      {/* COLUMN CONFIG */}
                      {!this.props.disabledFeatures?.['edit-cols'] && (
                        <ModalTriggerButton
                          buttonIcon="columns"
                          buttonText="Columns"
                          modalHeader="Manage Columns"
                          modalContent={this.columnEditor()}
                          onClick={() => {
                            const actionLocation = this.props.isInReport
                              ? 'report'
                              : 'runs table';
                            Analytics.track('Columns button clicked', {
                              location: actionLocation,
                            });
                          }}
                        />
                      )}
                    </span>
                  )}
                </div>
              </div>
            )}
            {/* TABLE MESSAGE */}
            <div className={`wb-table-message ${tableMessage ? 'active' : ''}`}>
              <div>{tableMessage}</div>
            </div>
            <div
              className="wb-table-grid-wrapper"
              onScroll={this.updateScroll}
              ref={this.props.expandable ? this.scrollRef : undefined}>
              {/* DATA TABLE */}
              <WBTableGridContainer {...gridProps} />
            </div>
          </div>
          {/* PAGINATION */}
          <WBTablePagination
            style={
              this.props.isResizingFullHeight ? {width: destinedWidth} : {}
            }
            loading={loading}
            currentPage={currentPage}
            maxPage={this.maxPage()}
            currentPageSize={this.tableSettings().pageSize}
            totalRowCount={totalRowCount}
            onClickPrevPage={() => {
              if (clearTableMessage) {
                clearTableMessage();
              }
              this.setCurrentPage(currentPage - 1);
            }}
            onClickNextPage={() => {
              if (clearTableMessage) {
                clearTableMessage();
              }
              const newPage = currentPage + 1;
              if (tableLoadMore != null) {
                tableLoadMore(newPage);
              }
              this.setCurrentPage(newPage);
            }}
            setPageSize={newPageSize => {
              this.setTableSettings({
                ...this.tableSettings(),
                pageSize: newPageSize,
              });
              this.resetPagination();
            }}
          />
          {this.props.expandable && !this.props.expanded && (
            <>
              <div
                className="wb-table-slider"
                onMouseDown={this.onSliderMouseDown}
              />
              {this.props.isResizingFullHeight && (
                <div className="dragging-overlay" />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  reload() {
    this.forceUpdate();
  }
}

function useWBTableProps() {
  const viewer = useViewer();
  const [onScreenRef, onScreen] = useInView({rootMargin: '500px'});
  const {
    columnResizingAccessor,
    isResizingFullHeight,
    setIsResizingFullHeight,
  } = useRunSelectorResizingContext();

  const workspaceDrawerContext = useWorkspaceDrawerContextMaybe();
  const handleSetIsResizingFullHeight = React.useCallback(
    (value: boolean) => {
      setIsResizingFullHeight(value);
      // If run selector is resizing, close workspace drawer because this will affect
      // panelBankWidth. panelBankWidth is used to determine which drawer to show, inline or floating
      if (value && workspaceDrawerContext?.contentType != null) {
        workspaceDrawerContext?.closeWorkspaceDrawer(true); // skipping animation
      }
    },
    [setIsResizingFullHeight, workspaceDrawerContext]
  );
  return {
    viewer,
    onScreenRef,
    onScreen,
    columnResizingAccessor,
    isResizingFullHeight,
    setIsResizingFullHeight: handleSetIsResizingFullHeight,
  };
}

export const WBTable = (
  props: WBTableProps & {tableRef?: React.RefObject<WBTableComp>}
) => {
  const selectedProps = useWBTableProps();
  return <WBTableComp ref={props.tableRef} {...props} {...selectedProps} />;
};
